import { App as AntApp, ConfigProvider } from 'antd';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { buildTheme } from './antdTheme';
import { prefs, THEME_KEY } from './storage';
import { themeColor, type ResolvedMode } from './tokens';

export type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeState {
  /** What the admin picked. */
  mode: ThemeMode;
  /** What is actually rendered. */
  resolved: ResolvedMode;
  setMode(mode: ThemeMode): void;
}

const Ctx = createContext<ThemeState | null>(null);
const DARK_QUERY = '(prefers-color-scheme: dark)';

function readMode(): ThemeMode {
  const v = prefs.get(THEME_KEY);
  // Deep Teal dark is the default, matching the app.
  return v === 'system' || v === 'light' || v === 'dark' ? v : 'dark';
}

function systemPrefersDark() {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/** Owns light/dark: persists the choice, follows the OS in "system", and syncs the document chrome. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedMode = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolved;
    root.style.colorScheme = resolved;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor[resolved]);
  }, [resolved]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    prefs.set(THEME_KEY, m);
  }, []);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);
  const config = useMemo(() => buildTheme(resolved), [resolved]);

  return (
    <Ctx.Provider value={value}>
      <ConfigProvider theme={config}>
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </Ctx.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useThemeMode must be used inside <ThemeProvider>');
  return ctx;
}
